import React, { useState, useEffect } from 'react';
import { Dimensions, Image, StyleSheet, Text, View } from 'react-native';
import { isEdge, isChrome, isSafari, isMobile, isTablet, isIOS } from 'react-device-detect';
import { luxColors } from '@alucio/lux-ui/src/theming/themes/legacy/colors';
import { isSupported as isTwilioSupported } from 'twilio-video';
import { DNABox, DNAButton, TextField } from '@alucio/lux-ui';
import { useLDClient } from 'launchdarkly-react-client-sdk';
const styles = StyleSheet.create({
    browserIcon: {
        height: 64,
        width: 64,
    },
    browserText: {
        color: luxColors.text.secondary,
        fontSize: 15,
        marginTop: 8,
    },
    browsersWrapper: {
        flexDirection: 'row',
        marginTop: 24,
    },
    browserWrapper: {
        alignItems: 'center',
        flexDirection: 'column',
        marginRight: 24,
    },
    image: {
        height: 112,
        width: 175,
    },
    mainWrapper: {
        alignItems: 'center',
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'center',
    },
    latestVersionText: {
        color: luxColors.subtitle.secondary,
        fontSize: 16,
        fontWeight: 'bold',
        textAlign: 'center',
        marginLeft: '2vw',
        marginRight: '2vw',
        maxWidth: 600,
    },
    notSupportedText: {
        color: luxColors.text.primary,
        fontSize: 24,
        marginLeft: 16,
        marginRight: 16,
        fontWeight: 'bold',
        marginBottom: 40,
        marginTop: 50,
        textAlign: 'center',
    },
    copyLinkContainer: {
        marginTop: 32,
    },
    copyLinkTextField: {
        backgroundColor: luxColors.info.primary,
        borderRadius: 8,
        borderColor: luxColors.disabled.primary,
        width: 210,
    },
});
const NOT_SUPPORTED_TEXTS = {
    beacon: {
        desktop: {
            title: 'This browser is incompatible.',
            description: 'For the best experience, Google Chrome is currently the supported browser for this application.',
            browsers: ['chrome'],
        },
        beaconWithEdge: {
            title: 'This browser is incompatible.',
            description: 'For the best experience, Google Chrome or Microsoft Edge is currently ' +
                'the supported browsers for this application.',
            browsers: ['chrome', 'edge'],
        },
        default: {
            title: 'This device is incompatible.',
            description: 'Please use the latest version of Google Chrome on a desktop or Apple Safari on an iPad.',
            browsers: ['chrome', 'safari'],
        },
        iOSTablet: {
            title: 'This browser is not supported.',
            description: 'Please use the latest version of Apple Safari.',
            browsers: ['safari'],
        },
    },
};
const BrowserChecker = (props) => {
    var _a;
    const { isMeetings, disable } = props;
    const [overrideBrowserCheck, setOverrideBrowserCheck] = useState(false);
    const [hasCopied, setHasCopied] = useState(false);
    const LDClient = useLDClient();
    const allowMsEdge = (_a = LDClient === null || LDClient === void 0 ? void 0 : LDClient.variation('allow-ms-edge', true)) !== null && _a !== void 0 ? _a : true;
    const [isEdgeEnabled, setIsEdgeEnabled] = useState(allowMsEdge);
    useEffect(() => {
        setIsEdgeEnabled(allowMsEdge);
    }, [allowMsEdge]);
    useEffect(() => {
        var _a;
        const queryParams = new URLSearchParams(window.location.search);
        if (queryParams.get('browsercheck') && ((_a = queryParams.get('browsercheck')) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'false') {
            setOverrideBrowserCheck(true);
        }
    }, []);
    const copyLinkToClipboard = () => {
        var _a;
        if (hasCopied) {
            return;
        }
        (_a = navigator === null || navigator === void 0 ? void 0 : navigator.clipboard) === null || _a === void 0 ? void 0 : _a.writeText(window.location.href).then(() => {
            setHasCopied(true);
            setTimeout(() => {
                setHasCopied(false);
            }, 3000);
        }).catch(err => {
            console.error('Could not copy text', err);
        });
    };
    if (disable) {
        return props.children;
    }
    const isDesktopSupportedBrowser = (isChrome || (isEdgeEnabled && isEdge));
    const isDesktop = (!isMobile && !isTablet);
    const isIOSSafari = (isIOS && isSafari);
    let isSupported = true;
    let errorScenario = '';
    // Meetings logic is now implemented within the meetings app
    if (!isMeetings) {
        errorScenario = isDesktop ? 'desktop'
            : isIOS && isTablet ? 'iOSTablet'
                : 'default';
        // For iOS PWA in Simulator isTwilioSupported always returns false even though on real device its true
        isSupported = (isIOS || isTwilioSupported) &&
            ((isDesktop && isDesktopSupportedBrowser) || (isTablet && isIOSSafari));
    }
    if (overrideBrowserCheck || isSupported) {
        return React.createElement(React.Fragment, null, props.children);
    }
    const windowHeight = Dimensions.get('window').height;
    const SELECTED_NOT_SUPPORTED_TEXT = NOT_SUPPORTED_TEXTS[isMeetings ? 'meetings' : (isEdgeEnabled && isEdge) ? 'beaconWithEdge' : 'beacon'][errorScenario];
    return (React.createElement(View, { style: [styles.mainWrapper, { height: windowHeight }] },
        React.createElement(Image, { source: props.unsupportedBrowserImage, style: styles.image }),
        React.createElement(Text, { style: styles.notSupportedText }, SELECTED_NOT_SUPPORTED_TEXT.title),
        React.createElement(Text, { style: styles.latestVersionText }, SELECTED_NOT_SUPPORTED_TEXT.description),
        React.createElement(DNABox, { appearance: "col", alignX: "center", spacing: "large", style: styles.copyLinkContainer },
            React.createElement(TextField.Kitten, { style: styles.copyLinkTextField, value: window.location.href, editable: false }),
            React.createElement(DNAButton, { status: hasCopied ? 'success' : 'primary', onPress: copyLinkToClipboard, iconLeft: hasCopied ? 'check' : undefined, size: "large" }, hasCopied ? 'Copied' : 'Copy link'))));
};
export default BrowserChecker;
//# sourceMappingURL=BrowserChecker.web.js.map